<template>
  <v-dialog :value="value" max-width="600px" persistent @input="close">
    <v-card>
      <v-card-title>
        <span class="headline">
          {{
            isEdit
              ? $t("riskAssessment.upload.editTitle")
              : $t("riskAssessment.upload.title")
          }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" class="pt-2">
          <div v-if="isEdit && currentFileName" class="mb-2 text-body-2">
            <v-icon small class="mr-1">mdi-file-outline</v-icon>
            {{ currentFileName }}
          </div>
          <v-file-input
            v-model="file"
            :label="
              isEdit
                ? $t('riskAssessment.upload.replaceFile')
                : $t('riskAssessment.upload.file')
            "
            :rules="isEdit ? [] : [(v) => !!v || $t('general.fieldNeedsToBeFilled')]"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            @change="onFile"
          ></v-file-input>
          <v-text-field
            v-model="form.title"
            :label="$t('riskAssessment.title')"
          ></v-text-field>
          <v-text-field
            v-model="form.location"
            :label="$t('general.location')"
          ></v-text-field>
          <v-autocomplete
            v-model="form.responsible"
            :items="userItems"
            :loading="getAllUsersStatus"
            item-text="Description"
            item-value="id"
            :label="$t('settings.responsible')"
            hide-no-data
            clearable
          ></v-autocomplete>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <div class="flex-grow-1"></div>
        <v-btn color="grey" outlined :disabled="saving" @click="close">
          {{ $t("general.back") }}
        </v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ isEdit ? $t("general.saveChanges") : $t("general.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

function emptyForm() {
  return { title: "", location: "", responsible: null };
}

export default {
  name: "riskAssessmentUploadDialog",
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null },
  },
  data: () => ({
    form: emptyForm(),
    file: null,
    document: null,
    saving: false,
  }),
  computed: {
    ...mapGetters(["getAllUsers", "getAllUsersStatus"]),
    isEdit() {
      return !!this.item;
    },
    currentFileName() {
      const doc = this.item && this.item.document;
      return doc ? doc.name : "";
    },
    userItems() {
      return this.getAllUsers.map((user) => ({
        ...user,
        Description: `${user.name} - ${user.short}`,
        id: user._id,
      }));
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.$store.dispatch("getUsers");
      this.file = null;
      this.document = null;
      if (this.item) {
        const responsible = this.item.responsible;
        this.form = {
          title: this.item.title || "",
          location: this.item.location || "",
          responsible:
            responsible && typeof responsible === "object"
              ? responsible._id
              : responsible || null,
        };
      } else {
        this.form = emptyForm();
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    close() {
      this.$emit("input", false);
    },
    async onFile(fileObject) {
      if (!fileObject) {
        this.document = null;
        return;
      }
      const match = fileObject.name.match(/\.([0-9a-z]+)$/i);
      const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(fileObject);
      });
      this.document = {
        name: fileObject.name.replace(/\.[0-9a-z]+$/i, ""),
        filetype: match ? match[1].toLowerCase() : "",
        content,
      };
      if (!this.form.title) this.form.title = this.document.name;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      if (!this.isEdit && !this.document) return;

      this.saving = true;
      try {
        const payload = {
          title: this.form.title,
          location: this.form.location,
          responsible: this.form.responsible || null,
        };
        if (this.document) payload.document = this.document;

        let result;
        if (this.isEdit) {
          result = await this.$store.dispatch("updateRiskAssessment", {
            ...payload,
            id: this.item._id || this.item.id,
          });
        } else {
          result = await this.$store.dispatch("createRiskAssessment", {
            ...payload,
            source: "uploaded",
            riskAssessmentCategory: this.$route.params.riskAssessmentCategoryId,
          });
        }
        if (result) {
          this.$emit("saved");
          this.close();
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
